/**
 * TanStack Query hooks for forms management.
 *
 * All forms are site-scoped. Each hook requires a siteSlug parameter.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { formsApi, CreateFormData, ListSubmissionsParams } from '../api';

// ============================================================================
// Query Keys
// ============================================================================

export const formKeys = {
  all: (siteSlug: string) => ['sites', siteSlug, 'forms'] as const,
  lists: (siteSlug: string) => [...formKeys.all(siteSlug), 'list'] as const,
  list: (siteSlug: string) => [...formKeys.lists(siteSlug)] as const,
  details: (siteSlug: string) => [...formKeys.all(siteSlug), 'detail'] as const,
  detail: (siteSlug: string, id: string) => [...formKeys.details(siteSlug), id] as const,
  html: (siteSlug: string, id: string) => [...formKeys.detail(siteSlug, id), 'html'] as const,
  submissions: (siteSlug: string, formId: string) =>
    [...formKeys.detail(siteSlug, formId), 'submissions'] as const,
};

// ============================================================================
// Forms Hooks
// ============================================================================

/** Fetch all forms for a site */
export function useForms(siteSlug: string) {
  return useQuery({
    queryKey: formKeys.list(siteSlug),
    queryFn: () => formsApi.list(siteSlug),
    enabled: !!siteSlug,
  });
}

/** Fetch a single form by ID with all steps and fields */
export function useForm(siteSlug: string, formId: string) {
  return useQuery({
    queryKey: formKeys.detail(siteSlug, formId),
    queryFn: () => formsApi.get(siteSlug, formId),
    enabled: !!siteSlug && !!formId,
  });
}

/** Fetch rendered HTML for a form */
export function useFormHtml(siteSlug: string, formId: string) {
  return useQuery({
    queryKey: formKeys.html(siteSlug, formId),
    queryFn: () => formsApi.getHtml(siteSlug, formId),
    enabled: !!siteSlug && !!formId,
  });
}

/** Create a new form */
export function useCreateForm(siteSlug: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateFormData) => formsApi.create(siteSlug, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: formKeys.all(siteSlug) });
    },
  });
}

/** Update an existing form */
export function useUpdateForm(siteSlug: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ formId, data }: { formId: string; data: CreateFormData }) =>
      formsApi.update(siteSlug, formId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: formKeys.all(siteSlug) });
      queryClient.invalidateQueries({
        queryKey: formKeys.detail(siteSlug, variables.formId),
      });
    },
  });
}

/** Delete a form */
export function useDeleteForm(siteSlug: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (formId: string) => formsApi.delete(siteSlug, formId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: formKeys.all(siteSlug) });
    },
  });
}

/** Delete (soft) a single submission — invalidates submissions + forms list so submission_count updates */
export function useDeleteFormSubmission(siteSlug: string, formId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (submissionId: string) =>
      formsApi.deleteSubmission(siteSlug, formId, submissionId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: formKeys.submissions(siteSlug, formId),
      });
      queryClient.invalidateQueries({ queryKey: formKeys.lists(siteSlug) });
    },
  });
}

/** Fetch submissions for a form */
export function useFormSubmissions(
  siteSlug: string,
  formId: string,
  params: ListSubmissionsParams = {}
) {
  return useQuery({
    queryKey: [...formKeys.submissions(siteSlug, formId), params],
    queryFn: () => formsApi.getSubmissions(siteSlug, formId, params),
    enabled: !!siteSlug && !!formId,
  });
}
